import AppColors from 'general/constants/AppColors';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

export const KTScrollCardDirections = {
    vertical: 'vertical',
    horizontal: 'horizontal',
};

KTScrollCard.propTypes = {
    children: PropTypes.element,
    containerHeight: PropTypes.string,
    containerWidth: PropTypes.string,
    scrollButtonStyle: PropTypes.object,
    direction: PropTypes.oneOf(Object.values(KTScrollCardDirections)),
};

function KTScrollCard({
    children,
    containerHeight = 'auto',
    containerWidth = 'auto',
    className = '',
    scrollButtonStyle = {},
    direction = KTScrollCardDirections.vertical,
}) {
    const columnRef = useRef(null);
    const cardRefs = useRef([]);
    const [showScrollNextButton, setShowScrollNextButton] = useState(false);
    const [showScrollPrevButton, setShowScrollPrevButton] = useState(false);

    const isHorizontal = direction === KTScrollCardDirections.horizontal;

    const setCardRef = (index) => (el) => {
        cardRefs.current[index] = el;
    };

    const checkScrollState = () => {
        if (!columnRef.current) return;

        const container = columnRef.current;
        const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = container;

        if (isHorizontal) {
            setShowScrollNextButton(scrollLeft + clientWidth < scrollWidth - 10);
            setShowScrollPrevButton(scrollLeft > 0);
        } else {
            setShowScrollNextButton(scrollTop + clientHeight < scrollHeight - 10);
            setShowScrollPrevButton(scrollTop > 0);
        }
    };

    const handleScrollNext = () => {
        if (!columnRef.current) return;

        const container = columnRef.current;
        const currentScroll = isHorizontal ? container.scrollLeft : container.scrollTop;

        const nextCard = cardRefs.current.find((card) => {
            if (!card) return false;
            const offset = isHorizontal ? card.offsetLeft : card.offsetTop;
            return offset > currentScroll + 10;
        });

        if (nextCard) {
            const offset = isHorizontal ? nextCard.offsetLeft : nextCard.offsetTop;
            container.scrollTo({
                [isHorizontal ? 'left' : 'top']: offset,
                behavior: 'smooth',
            });
        } else {
            const maxScroll = isHorizontal
                ? container.scrollWidth - container.clientWidth
                : container.scrollHeight - container.clientHeight;

            container.scrollTo({
                [isHorizontal ? 'left' : 'top']: maxScroll,
                behavior: 'smooth',
            });
        }
    };

    const handleScrollPrev = () => {
        if (!columnRef.current) return;

        const container = columnRef.current;
        const currentScroll = isHorizontal ? container.scrollLeft : container.scrollTop;

        const previousCards = cardRefs.current.filter((card) => {
            if (!card) return false;
            const offset = isHorizontal ? card.offsetLeft : card.offsetTop;
            return offset < currentScroll - 10;
        });

        if (previousCards.length > 0) {
            const prevCard = previousCards[previousCards.length - 1];
            const offset = isHorizontal ? prevCard.offsetLeft : prevCard.offsetTop;
            container.scrollTo({
                [isHorizontal ? 'left' : 'top']: offset,
                behavior: 'smooth',
            });
        } else {
            container.scrollTo({
                [isHorizontal ? 'left' : 'top']: 0,
                behavior: 'smooth',
            });
        }
    };


    useEffect(() => {
        const container = columnRef.current;

        const initialCheck = () => {
            checkScrollState();
        };

        const timeoutId = setTimeout(initialCheck, 0);

        const handleScroll = () => {
            checkScrollState();
        };

        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        const resizeObserver = new ResizeObserver(() => {
            checkScrollState();
        });

        if (container) {
            resizeObserver.observe(container);
        }

        return () => {
            clearTimeout(timeoutId);
            if (container) {
                container.removeEventListener('scroll', handleScroll);
                resizeObserver.unobserve(container);
            }
        };
    }, [isHorizontal]);

    useEffect(() => {
        checkScrollState();
    }, [children, containerHeight, containerWidth, isHorizontal]);

    const childrenWithRefs = React.Children.map(children, (child, index) => (
        <div
            ref={setCardRef(index)}
            key={index}
            style={{
                flexShrink: 0,
                ...(isHorizontal && { width: '100%' }),
            }}
        >
            {child}
        </div>
    ));

    const containerStyle = {
        height: isHorizontal ? '100%' : containerHeight,
        width: isHorizontal ? containerWidth : '100%',
        overflowX: isHorizontal ? 'auto' : 'hidden',
        overflowY: isHorizontal ? 'hidden' : 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
    };

    const getButtonPosition = (isNext) => {
        const base = {
            position: 'absolute',
            backgroundColor: '#fff',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0px 5px 15px 0px #00000026',
            zIndex: 10,
            ...scrollButtonStyle,
        };

        if (isHorizontal) {
            return {
                ...base,
                top: '50%',
                transform: 'translateY(-50%)',
                ...(isNext ? { right: '10px' } : { left: '10px' }),
            };
        } else {
            return {
                ...base,
                left: '50%',
                transform: 'translateX(-50%)',
                ...(isNext ? { bottom: '10px' } : { top: '10px' }),
            };
        }
    };

    return (
        <div className={`position-relative ${className}`} style={{ position: 'relative' }}>
            <div
                ref={columnRef}
                style={containerStyle}
                className="hide-scrollbar"
            >
                {childrenWithRefs}
            </div>

            {showScrollNextButton && (
                <button onClick={handleScrollNext} style={getButtonPosition(true)}>
                    <i
                        className={`fas fa-arrow-${isHorizontal ? 'right' : 'down'}`}
                        style={{ color: AppColors.gray[800] }}
                    />
                </button>
            )}

            {showScrollPrevButton && (
                <button onClick={handleScrollPrev} style={getButtonPosition(false)}>
                    <i
                        className={`fas fa-arrow-${isHorizontal ? 'left' : 'up'}`}
                        style={{ color: AppColors.gray[800] }}
                    />
                </button>
            )}
        </div>
    );
};

export default KTScrollCard;